import { useDocumentVisibility, useIntervalFn } from '@vueuse/core';
import { computed, watch } from 'vue';

import { crossbeamApi } from '@/api';
import useMinimalApp from '@/composables/useMinimalApp';
import { useRootStore } from '@/stores/RootStore';

const ACTIVITY_PATH = '/v0.1/users/me/activity';
const PING_INTERVAL_MS = 4 * 60 * 60 * 1000;

export default function useUserActivityPing() {
  const rootStore = useRootStore();
  const { isMinimalView } = useMinimalApp();
  const documentVisibility = useDocumentVisibility();
  const isEnabled = computed(() => rootStore.hasProfile && !isMinimalView);
  let lastPingAt: number | null = null;

  function hasPingedRecently() {
    return lastPingAt !== null && Date.now() - lastPingAt < PING_INTERVAL_MS;
  }

  async function sendPing() {
    try {
      if (documentVisibility.value !== 'visible' || hasPingedRecently()) return;
      lastPingAt = Date.now();
      await crossbeamApi.POST(ACTIVITY_PATH);
    } catch {
      // Silent by design: no retries and no user-facing errors
    }
  }

  const { pause, resume } = useIntervalFn(sendPing, PING_INTERVAL_MS, {
    immediate: false,
  });

  watch(isEnabled, (enabled) => (enabled ? resume() : pause()), {
    immediate: true,
  });

  watch(
    [isEnabled, documentVisibility],
    ([enabled, visibility]) => {
      if (enabled && visibility === 'visible') sendPing();
    },
    { immediate: true },
  );
}
